/* eslint-disable no-unused-expressions */

import React from "react";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";

/* Shared label + field wrapper */
function Field({ id, label, children }) {
  return (
    <div className="sys-form-field" key={id}>
      <label htmlFor={id} className="sys-form-label">
        {label}
      </label>
      {children}
    </div>
  );
}

function renderTextBox(input, props) {
  return (
    <Field id={input.id} label={input.label} key={input.id}>
      <InputText
        id={input.id}
        name={input.id}
        value={props.data[input.id] ?? ""}
        className="w-100"
        type={input.type}
        disabled={props.isLoading || input.disabled}
        tooltip={input.tooltip}
        onChange={(e) => {
          let val = e.target.value;
          if (input.maxValue !== undefined && val !== "" && parseFloat(val) > input.maxValue) {
            val = String(input.maxValue);
          }
          props.data[e.target.id] = val;
          props.updateForm(e.target.id, val);
        }}
      />
    </Field>
  );
}

// Multi-line text. `rows` defaults to 4 and `maxLength` is optional, the columns these
// bind to are nvarchar(max) so no limit is imposed unless a caller asks for one.
function renderTextArea(input, props) {
  return (
    <Field id={input.id} label={input.label} key={input.id}>
      <InputTextarea
        id={input.id}
        name={input.id}
        value={props.data[input.id] ?? ""}
        className="w-100"
        rows={input.rows ?? 4}
        maxLength={input.maxLength}
        autoResize={input.autoResize ?? true}
        disabled={props.isLoading || input.disabled}
        tooltip={input.tooltip}
        onChange={(e) => {
          props.data[e.target.id] = e.target.value;
          props.updateForm(e.target.id, e.target.value);
        }}
      />
    </Field>
  );
}

function renderFileUpload(input, props) {
  return (
    <Field id={input.id} label={input.label} key={input.id}>
      <input
        id={input.id}
        name={input.id}
        type="file"
        className="form-control"
        disabled={props.isLoading || input.disabled}
        onChange={(e) => {
          props.data[e.target.id] = e.target.files[0];
          props.updateForm(e.target.id, e.target.files[0]);
        }}
      />
    </Field>
  );
}

function renderMultipleFileUpload(input, props) {
  return (
    <Field id={input.id} label={input.label} key={input.id}>
      <input
        id={input.id}
        name={input.id}
        type="file"
        className="form-control"
        disabled={props.isLoading || input.disabled}
        onChange={(e) => {
          props.data[e.target.id] = e.target.files;
          props.updateForm(e.target.id, e.target.files);
        }}
        multiple
      />
    </Field>
  );
}

function renderDatePicker(input, props) {
  return (
    <Field id={input.id} label={input.label} key={input.id}>
      <Calendar
        id={input.id}
        name={input.id}
        value={props.data[input.id]}
        className="w-100"
        hourFormat={input.hourFormat}
        dateFormat={input.dateFormat}
        disabled={props.isLoading || input.disabled}
        tooltip={input.tooltip}
        showIcon
        onChange={(e) => {
          props.data[e.target.id] = e.target.value;
          props.updateForm(e.target.id, e.target.value);
        }}
      />
    </Field>
  );
}

function renderTimePicker(input, props) {
  return (
    <Field id={input.id} label={input.label} key={input.id}>
      <Calendar
        id={input.id}
        name={input.id}
        value={props.data[input.id] ? new Date(props.data[input.id]) : null}
        className="w-100"
        hourFormat={input.hourFormat}
        disabled={props.isLoading || input.disabled}
        tooltip={input.tooltip}
        timeOnly
        showTime
        onChange={(e) => {
          const d = e.value;
          const pad = (n) => String(n).padStart(2, "0");
          const localIso =
            `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
            `T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
          props.data[e.target.id] = localIso;
          props.updateForm(e.target.id, localIso);
        }}
      />
    </Field>
  );
}

function renderDropdown(input, props) {
  return (
    <Field id={input.id} label={input.label} key={input.id}>
      <Dropdown
        id={input.id}
        name={input.id}
        value={props.data[input.id]}
        showClear={true}
        options={input.options}
        disabled={props.isLoading || input.disabled}
        filter={true}
        tooltip={input.tooltip}
        className="w-100"
        onChange={(e) => {
          props.data[e.target.id] = e.target.value;
          props.updateForm(e.target.id, e.target.value);
          input.customOnChangeHandler
            ? input.customOnChangeHandler(e.target.value)
            : null;
        }}
      />
    </Field>
  );
}

function renderMultiSelect(input, props) {
  return (
    <Field id={input.id} label={input.label} key={input.id}>
      <MultiSelect
        id={input.id}
        name={input.id}
        value={props.data[input.id]}
        tooltip={input.tooltip}
        options={input.options}
        disabled={props.isLoading || input.disabled}
        className="w-100"
        filter={true}
        display="chip"
        onChange={(e) => {
          props.data[e.target.id] = e.target.value;
          props.updateForm(e.target.id, e.target.value);
          input.customOnChangeHandler
            ? input.customOnChangeHandler(e.target.value)
            : null;
        }}
      />
    </Field>
  );
}

function renderCheckBox(input, props) {
  return (
    <Field id={input.id} label={input.label} key={input.id}>
      <div style={{ paddingTop: 4 }}>
        <Checkbox
          id={input.id}
          name={input.id}
          checked={props.data[input.id]}
          tooltip={input.tooltip}
          disabled={props.isLoading || input.disabled}
          onChange={(e) => {
            props.data[e.target.id] = e.checked;
            props.updateForm(e.target.id, e.checked);
          }}
        />
      </div>
    </Field>
  );
}

function renderInput(input, props) {
  const t = input.type;
  if (t === "text" || t === "password")   return renderTextBox(input, props);
  if (t === "textarea")                   return renderTextArea(input, props);
  if (t === "dropdown")                   return renderDropdown(input, props);
  if (t === "multiselect")                return renderMultiSelect(input, props);
  if (t === "datetime")                   return renderDatePicker(input, props);
  if (t === "time")                       return renderTimePicker(input, props);
  if (t === "checkbox")                   return renderCheckBox(input, props);
  if (t === "fileupload")                 return renderFileUpload(input, props);
  if (t === "multiplefileupload")         return renderMultipleFileUpload(input, props);
  return null;
}

const sysForm = (props) => {
  const left = [];
  const right = [];

  props.inputs.forEach((input, i) => {
    (i % 2 === 0 ? left : right).push(renderInput(input, props));
  });

  const isTwoCol = props.inputs.length % 2 === 0 || props.inputs.length > 6;

  if (isTwoCol) {
    return (
      <div className="row g-3">
        <div className="col-sm-6">{left}</div>
        <div className="col-sm-6">{right}</div>
      </div>
    );
  }
  return (
    <div className="row g-3">
      <div className="col-sm-12">{left}{right}</div>
    </div>
  );
};

export default sysForm;
